import React, { useEffect, useState } from 'react';
import { Truck, MapPin, Package, Calendar, Euro, CheckCircle2 } from 'lucide-react';
import { getRecentCompletedDeliveries } from '../services/api';

const formatCompletedDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' });
};

const Row = ({ Icon, children, testId }) => (
  <div className="flex items-start gap-2 text-sm text-gray-700" data-testid={testId}>
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
    <span className="min-w-0">{children}</span>
  </div>
);

/**
 * Read-only feed of the five most recent verified deliveries across the
 * platform. Shown under the driver's live jobs list as social proof — the
 * backend strips every identifying detail before it reaches us.
 */
export const RecentCompletedDeliveries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getRecentCompletedDeliveries()
      .then(({ data }) => { if (active) setItems(data.deliveries || []); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return null;

  return (
    <section className="mt-10 pt-8 border-t border-gray-200" data-testid="recent-completed-section">
      <h2 className="text-lg md:text-lg font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
        <Truck className="w-5 h-5 text-[#2E7D32]" strokeWidth={2} />
        Recent Deliveries on JustDeliver
      </h2>
      <p className="text-sm text-gray-500 mt-1">See recent deliveries completed by drivers across Ireland.</p>

      {items.length === 0 ? (
        <p
          className="mt-4 text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-xl px-4 py-5"
          data-testid="recent-completed-empty"
        >
          Completed deliveries will appear here as drivers complete jobs through JustDeliver.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((d, i) => (
            <article
              key={`${d.completed_at}-${i}`}
              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-sm transition-colors"
              data-testid={`recent-completed-card-${i}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold uppercase tracking-wide text-[#1B5E20]">
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                  Delivery completed
                </span>
              </div>

              <div className="space-y-2">
                <Row Icon={MapPin} testId={`recent-completed-route-${i}`}>
                  <span className="font-semibold text-gray-900">{d.pickup_area}</span>
                  <span className="text-gray-400 mx-1.5">→</span>
                  <span className="font-semibold text-gray-900">{d.dropoff_area}</span>
                </Row>
                {d.item_description && (
                  <Row Icon={Package} testId={`recent-completed-item-${i}`}>
                    <span className="text-gray-500">Item: </span>{d.item_description}
                  </Row>
                )}
                <Row Icon={Calendar} testId={`recent-completed-date-${i}`}>
                  <span className="text-gray-500">Completed: </span>
                  {formatCompletedDate(d.completed_at) || '—'}
                </Row>
                <Row Icon={Euro} testId={`recent-completed-price-${i}`}>
                  <span className="text-gray-500">Agreed price: </span>
                  <span className="font-extrabold text-[#1B5E20] tabular-nums">€{Number(d.agreed_price).toFixed(2)}</span>
                </Row>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default RecentCompletedDeliveries;
